import { useMemo, useState } from 'react';
import { Plus, Trash2, Mail, MessageSquare, Calendar, Users, User } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConnectionIndicator,
  ProviderLogo,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import {
  useMyCredentials,
  useSpaceCredentials,
  useStartOAuth,
} from '@/hooks/useIdentityCredentials';
import { isDeviceStart, openIdentityOAuthUrl } from '@/pages/credentials/identity-lib';
import {
  useGoogleConnections,
  useGoogleWatches,
  useCreateGoogleWatch,
  useDeleteGoogleWatch,
  useListGoogleCalendars,
} from '@/hooks/useGoogleWatches';
import {
  useIntegrationEvents,
  useIntegrationEventDetail,
} from '@/hooks/useIntegrations';
import {
  useIntegrationLiveEvents,
  type LiveIntegrationEvent,
} from '@/hooks/useIntegrationLiveEvents';
import { useMeSpaces } from '@/hooks/useMeSpaces';
import { usePlatformEvents } from '@/contexts/PlatformEventContext';
import { useSpaceId, useSpaceContext } from '@/contexts/SpaceContext';
import { routes } from '@/lib/routes';
import BackLink from '@/components/navigation/BackLink';
import {
  mergeRows,
  DeliveryRowView,
  type DeliveryRow,
} from './IntegrationDeliveriesCard';
import { GoogleSpaceStats } from './GoogleConnectionStats';
import type { Integration, IntegrationEvent } from '@/types/integrations';
import type { Credential } from '@/types/identity';
import type { GoogleCalendar, GoogleWatch } from '@/types/google';

/**
 * Per-space "Connect Gmail / Chat" page. Gmail/Chat are connected here (not via
 * the generic webhook creator): pick a connected Google account (or sign in),
 * then attach a mailbox / Chat space that routes events to this space.
 */
export default function GoogleConnect() {
  const spaceId = useSpaceId();
  const { isPersonal } = useSpaceContext();
  const connectionsQ = useGoogleConnections();

  // Credential tier follows the space: personal space → the caller's own (user)
  // credentials; shared space → credentials connected *to the space*. This is
  // why a shared space never surfaces (or attaches) someone's personal inbox —
  // to use a personal account there you'd add a Google token to the space.
  const myCredsQ = useMyCredentials();
  const spaceCredsQ = useSpaceCredentials(spaceId);
  const credsQ = isPersonal ? myCredsQ : spaceCredsQ;
  const oauthScope: 'user' | { spaceId: string } = isPersonal ? 'user' : { spaceId };
  const principalKind: 'user' | 'space-service' = isPersonal ? 'user' : 'space-service';

  const gmailConn = connectionsQ.data?.find((c) => c.provider === 'google-gmail');
  const chatConn = connectionsQ.data?.find((c) => c.provider === 'google-chat');
  const calendarConn = connectionsQ.data?.find((c) => c.provider === 'google-calendar');

  // Space-scoped stats come from this space's watches (each routes to one
  // space), not the org-wide connection counters. react-query dedups these with
  // the per-section watch queries.
  const gmailWatchesQ = useGoogleWatches(gmailConn?.integrationId);
  const chatWatchesQ = useGoogleWatches(chatConn?.integrationId);
  const calendarWatchesQ = useGoogleWatches(calendarConn?.integrationId);
  const spaceWatches = [
    ...(gmailWatchesQ.data ?? []),
    ...(chatWatchesQ.data ?? []),
    ...(calendarWatchesQ.data ?? []),
  ];

  // Connected Google accounts in the active tier (multi-account).
  const googleAccounts = useMemo(
    () =>
      (credsQ.data?.credentials ?? []).filter(
        (c) => c.provider === 'google' && c.status === 'connected',
      ),
    [credsQ.data],
  );

  if (connectionsQ.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  if (!gmailConn && !chatConn && !calendarConn) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-lg font-semibold text-foreground">Google Workspace</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          No Google Workspace connection has been set up for your organization yet.
          Ask an org admin to configure the connection before connecting a mailbox,
          Chat space, or calendar here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackLink to={routes.console.spaceIntegrations(spaceId)}>Integrations</BackLink>
      <div className="flex items-center gap-4">
        <ProviderLogo
          slug="google-workspace"
          displayName="Google Workspace"
          className="h-12 w-12"
          fallbackClassName="text-sm"
        />
        <div>
          <h1 className="text-2xl font-bold text-foreground">Google Workspace</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Connect a Gmail mailbox, a Google Chat space, or a Google Calendar; its
            events route into this space. Agents listen by filtering on the
            mailbox / space / calendar.
          </p>
        </div>
      </div>

      <GoogleSpaceStats watches={spaceWatches} />

      <GoogleAccountPicker
        accounts={googleAccounts}
        loading={credsQ.isLoading}
        scope={oauthScope}
        isPersonal={isPersonal}
      />

      {gmailConn && (
        <GmailSection
          connection={gmailConn}
          accounts={googleAccounts}
          principalKind={principalKind}
        />
      )}
      {chatConn && (
        <ChatSection
          connection={chatConn}
          accounts={googleAccounts}
          principalKind={principalKind}
          isPersonal={isPersonal}
        />
      )}
      {calendarConn && (
        <CalendarSection
          connection={calendarConn}
          accounts={googleAccounts}
          principalKind={principalKind}
        />
      )}

      <GoogleDeliveriesCard
        spaceId={spaceId}
        gmailConn={gmailConn}
        chatConn={chatConn}
        calendarConn={calendarConn}
      />
    </div>
  );
}

function GoogleAccountPicker({
  accounts,
  loading,
  scope,
  isPersonal,
}: {
  accounts: Credential[];
  loading: boolean;
  scope: 'user' | { spaceId: string };
  isPersonal: boolean;
}) {
  const { error } = useToast();
  const startOAuth = useStartOAuth();

  const connect = async () => {
    try {
      const res = await startOAuth.mutateAsync({
        scope,
        provider: 'google',
        body: {
          redirectUri: `${window.location.origin}/oauth/identity-callback`,
          returnUrl: `${window.location.origin}/oauth/connected`,
        },
      });
      // Google is redirect-only; the device branch is unreachable but keeps
      // the discriminated union narrowed.
      if (isDeviceStart(res)) return;
      openIdentityOAuthUrl(res.authorizationUrl, {
        provider: 'google',
        scope: scope === 'user' ? 'user' : 'space',
      });
    } catch (e) {
      error(e instanceof Error ? e.message : 'Could not start Google sign-in');
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">
            {isPersonal ? 'Your Google accounts' : 'Space Google accounts'}
          </h2>
        </div>
        <Button size="sm" variant="outline" onClick={connect} isLoading={startOAuth.isPending}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          {accounts.length === 0 ? 'Connect Google' : 'Connect another'}
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : accounts.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {isPersonal
            ? 'No Google account connected yet.'
            : 'No Google account connected to this space yet.'}
        </p>
      ) : (
        <ul className="border border-border rounded-lg divide-y divide-border bg-card">
          {accounts.map((a) => (
            <li key={a.credId} className="flex items-center gap-3 px-3 py-2">
              <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="flex-1 min-w-0 text-sm text-foreground truncate">
                {a.metadata.externalUsername ?? a.metadata.accountName ?? a.credId}
              </span>
            </li>
          ))}
        </ul>
      )}

      {!isPersonal && (
        <p className="text-[11px] text-muted-foreground">
          Shared spaces use accounts connected to the space — never your personal
          inbox. To use a personal account here, connect it to the space.
        </p>
      )}
    </section>
  );
}

/** A Gmail account row: the account paired with its live watch (if any). */
interface MailboxRow {
  key: string;
  accountId?: string;
  email: string;
  watch?: GoogleWatch;
}

function GmailSection({
  connection,
  accounts,
  principalKind,
}: {
  connection: Integration;
  accounts: Credential[];
  principalKind: 'user' | 'space-service';
}) {
  const { success, error } = useToast();
  const watchesQ = useGoogleWatches(connection.integrationId);
  const create = useCreateGoogleWatch(connection.integrationId);
  const del = useDeleteGoogleWatch(connection.integrationId);

  const watches = watchesQ.data ?? [];

  // One row per Google account, paired with its live watch (matched on the
  // account's providerInstanceId, or defensively on the mailbox email which is
  // the Gmail watch's resourceId). The row's right-side action is Connect when
  // unpaired, or Disconnect (trash) when a watch exists. Watches with no known
  // account (e.g. space-service principals) are appended as delete-only rows.
  const matchedWatchIds = new Set<string>();
  const accountRows: MailboxRow[] = accounts.map((a) => {
    const accountId = a.providerInstanceId;
    const email =
      a.metadata.externalUsername ?? a.metadata.accountName ?? a.credId;
    const watch = watches.find(
      (w) =>
        w.status !== 'expired' &&
        ((!!accountId && w.accountId === accountId) ||
          (w.resourceId ?? '').toLowerCase() === email.toLowerCase()),
    );
    if (watch) matchedWatchIds.add(watch.watchId);
    return { key: a.credId, accountId, email, watch };
  });
  const orphanRows: MailboxRow[] = watches
    .filter((w) => !matchedWatchIds.has(w.watchId))
    .map((w) => ({
      key: w.watchId,
      accountId: w.accountId,
      email: w.resourceId,
      watch: w,
    }));
  const rows = [...accountRows, ...orphanRows];

  const connect = async (accountId?: string) => {
    try {
      await create.mutateAsync({ principalKind, ...(accountId ? { accountId } : {}) });
      success('Mailbox connected');
    } catch (e) {
      error(e instanceof Error ? e.message : 'Could not connect mailbox');
    }
  };

  return (
    <section className="space-y-4">
      <SurfaceHeader
        icon={<Mail className="h-4 w-4 text-muted-foreground" />}
        title="Gmail"
        status={connection.status}
      />

      <Card>
        <CardHeader
          title="Mailboxes"
          description="Attach a mailbox; its messages route into this space."
        />
        <div className="space-y-3">
          {rows.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Connect a Google account above, then attach its mailbox here.
            </p>
          ) : (
            <ul className="border border-border rounded-lg divide-y divide-border bg-card">
              {rows.map((row) => {
                const connected = !!row.watch;
                return (
                  <li key={row.key} className="flex items-center gap-3 px-3 py-2">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="flex-1 min-w-0 text-sm text-foreground truncate">
                      {row.email}
                    </span>
                    {connected ? (
                      <>
                        <Badge variant={getStatusVariant(row.watch!.status)}>
                          {row.watch!.status}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => del.mutate(row.watch!.watchId)}
                          isLoading={del.isPending && del.variables === row.watch!.watchId}
                          aria-label={`Disconnect ${row.email}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => connect(row.accountId)}
                        isLoading={
                          create.isPending && create.variables?.accountId === row.accountId
                        }
                        aria-label={`Connect ${row.email}`}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Connect
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-[11px] text-muted-foreground">
            Agents listen with eventType{' '}
            <code>com.google.gmail.message.received</code> and a filter on{' '}
            <code>mailbox = &lt;email&gt;</code>.
          </p>
        </div>
      </Card>
    </section>
  );
}

function ChatSection({
  connection,
  accounts,
  principalKind,
  isPersonal,
}: {
  connection: Integration;
  accounts: Credential[];
  principalKind: 'user' | 'space-service';
  isPersonal: boolean;
}) {
  const { success, error } = useToast();
  const watchesQ = useGoogleWatches(connection.integrationId);
  const create = useCreateGoogleWatch(connection.integrationId);
  const del = useDeleteGoogleWatch(connection.integrationId);
  const [resourceId, setResourceId] = useState('');
  const [mode, setMode] = useState<'all' | 'mention'>('all');
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const watches = watchesQ.data ?? [];

  // DM watches (1:1 Operator DM) are presented in their own card; everything
  // else is a room/space subscription.
  const dmWatches = watches.filter((w) => w.dm);
  const roomWatches = watches.filter((w) => !w.dm);

  // Default the account selector to the first connected account.
  const selectedAccountId =
    accountId ?? accounts[0]?.providerInstanceId ?? undefined;
  const hasAccount = accounts.length > 0;

  const connectRoom = async () => {
    if (!resourceId.trim()) return;
    try {
      await create.mutateAsync({
        principalKind,
        resourceId: resourceId.trim(),
        mode,
        ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
      });
      success('Chat space subscribed');
      setResourceId('');
    } catch (e) {
      error(e instanceof Error ? e.message : 'Could not subscribe to the Chat space');
    }
  };

  // DM → personal: the backend find-or-creates the user's DM with the Operator
  // app (no resourceId), so the user never has to open Chat and message it.
  const connectDm = async () => {
    try {
      await create.mutateAsync({
        principalKind: 'user',
        ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
      });
      success('Operator DM connected');
    } catch (e) {
      error(e instanceof Error ? e.message : 'Could not connect the Operator DM');
    }
  };

  return (
    <section className="space-y-4">
      <SurfaceHeader
        icon={<MessageSquare className="h-4 w-4 text-muted-foreground" />}
        title="Google Chat"
        status={connection.status}
      />

      {isPersonal && (
        <Card>
          <CardHeader
            title="Direct messages"
            description="Connect your 1:1 DM with Operator; messages you send it route into this space. Operator finds-or-creates the DM, so you never have to open Chat."
          />
          <div className="space-y-3">
            {dmWatches.length > 0 && (
              <ul className="border border-border rounded-lg divide-y divide-border bg-card">
                {dmWatches.map((w) => (
                  <li key={w.watchId} className="flex items-center gap-3 px-3 py-2">
                    <MessageSquare className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="flex-1 min-w-0 text-sm text-foreground truncate">
                      Operator direct messages
                    </span>
                    <Badge variant={getStatusVariant(w.status)}>{w.status}</Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => del.mutate(w.watchId)}
                      isLoading={del.isPending && del.variables === w.watchId}
                      aria-label="Disconnect Operator DM"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {!hasAccount ? (
              <p className="text-xs text-muted-foreground">
                Connect a Google account above, then connect your Operator DM here.
              </p>
            ) : dmWatches.length === 0 ? (
              <div className="space-y-2 max-w-md">
                <ChatAccountSelect
                  accounts={accounts}
                  value={selectedAccountId}
                  onChange={setAccountId}
                />
                <div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={connectDm}
                    isLoading={create.isPending && !create.variables?.resourceId}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Connect my Operator DM
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Chat space subscriptions"
          description="Subscribe to a Chat space (e.g. a support room); its messages route into this space. Each subscription is member-gated by the connecting Google account."
        />
        <div className="space-y-3">
          {!hasAccount ? (
            <p className="text-xs text-muted-foreground">
              Connect a Google account above, then subscribe to a Chat space here.
            </p>
          ) : (
            <div className="space-y-2 max-w-md">
              <ChatAccountSelect
                accounts={accounts}
                value={selectedAccountId}
                onChange={setAccountId}
              />
              <div className="flex gap-2">
                <input
                  className="flex-1 text-sm border border-border rounded-md bg-background px-2 py-1.5"
                  placeholder="spaces/AAAAxxxx"
                  value={resourceId}
                  onChange={(e) => setResourceId(e.target.value)}
                />
                <select
                  className="text-sm border border-border rounded-md bg-background px-2 py-1.5"
                  value={mode}
                  onChange={(e) => setMode(e.target.value as 'all' | 'mention')}
                  aria-label="Delivery mode"
                >
                  <option value="all">All messages</option>
                  <option value="mention">@Operator only</option>
                </select>
                <Button
                  size="sm"
                  onClick={connectRoom}
                  isLoading={create.isPending && !!create.variables?.resourceId}
                  disabled={!resourceId.trim()}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Subscribe
                </Button>
              </div>
            </div>
          )}
          <WatchList
            watches={roomWatches}
            emptyLabel="No Chat spaces subscribed for this space yet."
            onDelete={(id) => del.mutate(id)}
            deleting={del.isPending}
          />
          <p className="text-[11px] text-muted-foreground">
            Agents listen with eventType{' '}
            <code>com.google.chat.message.created</code> and a filter on{' '}
            <code>space</code>.
          </p>
        </div>
      </Card>
    </section>
  );
}

/** Label a calendar in the picker: name + a primary/role hint. */
function calendarLabel(c: GoogleCalendar): string {
  const name = c.summary || c.id;
  if (c.primary) return `${name} (primary)`;
  if (c.accessRole && c.accessRole !== 'owner') return `${name} (${c.accessRole})`;
  return name;
}

/**
 * Per-space Calendar card. Modeled on GmailSection (account → resource → watch),
 * but the resource is chosen from a picker of the account's calendars (primary +
 * secondary + shared) rather than auto-paired. Read-only surface, so there's no
 * label / mode / loop-breaker control. A calendar can route to many spaces, but
 * not twice into one — the backend returns 409, surfaced as a friendly toast.
 */
function CalendarSection({
  connection,
  accounts,
  principalKind,
}: {
  connection: Integration;
  accounts: Credential[];
  principalKind: 'user' | 'space-service';
}) {
  const { success, error } = useToast();
  const watchesQ = useGoogleWatches(connection.integrationId);
  const create = useCreateGoogleWatch(connection.integrationId);
  const del = useDeleteGoogleWatch(connection.integrationId);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [calendarId, setCalendarId] = useState('');

  const watches = watchesQ.data ?? [];
  const hasAccount = accounts.length > 0;
  const selectedAccountId = accountId ?? accounts[0]?.providerInstanceId ?? undefined;

  const calendarsQ = useListGoogleCalendars(
    connection.integrationId,
    selectedAccountId,
    principalKind,
  );
  const calendars = useMemo(() => calendarsQ.data ?? [], [calendarsQ.data]);

  // Calendar watches carry no displayName — resolve the picker's summary by id
  // so connected rows show a friendly name instead of the raw calendar id.
  const summaryById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of calendars) if (c.summary) m.set(c.id, c.summary);
    return m;
  }, [calendars]);

  // A calendar already routing into this space can't be added again.
  const connectedIds = new Set(watches.map((w) => w.resourceId));
  const available = calendars.filter((c) => !connectedIds.has(c.id));
  const watchRows = watches.map((w) => ({
    ...w,
    displayName: w.displayName || summaryById.get(w.resourceId),
  }));

  const connect = async () => {
    const id = calendarId.trim();
    if (!id) return;
    try {
      await create.mutateAsync({
        principalKind,
        resourceId: id,
        ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
      });
      success('Calendar connected');
      setCalendarId('');
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      error(
        /409|already/i.test(msg)
          ? 'That calendar is already connected to this space.'
          : msg || 'Could not connect the calendar',
      );
    }
  };

  return (
    <section className="space-y-4">
      <SurfaceHeader
        icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
        title="Google Calendar"
        status={connection.status}
      />

      <Card>
        <CardHeader
          title="Calendars"
          description="Attach a calendar (primary, secondary, or shared); its events route into this space. Read-only — agents don't write back."
        />
        <div className="space-y-3">
          {!hasAccount ? (
            <p className="text-xs text-muted-foreground">
              Connect a Google account above, then attach a calendar here.
            </p>
          ) : (
            <div className="space-y-2 max-w-md">
              <ChatAccountSelect
                accounts={accounts}
                value={selectedAccountId}
                onChange={setAccountId}
              />
              <div className="flex gap-2">
                <select
                  className="flex-1 text-sm border border-border rounded-md bg-background px-2 py-1.5"
                  value={calendarId}
                  onChange={(e) => setCalendarId(e.target.value)}
                  disabled={calendarsQ.isLoading || available.length === 0}
                  aria-label="Calendar to connect"
                >
                  <option value="">
                    {calendarsQ.isLoading
                      ? 'Loading calendars…'
                      : calendars.length === 0
                        ? 'No calendars found'
                        : available.length === 0
                          ? 'All calendars connected'
                          : 'Select a calendar…'}
                  </option>
                  {available.map((c) => (
                    <option key={c.id} value={c.id}>
                      {calendarLabel(c)}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={connect}
                  isLoading={create.isPending}
                  disabled={!calendarId.trim()}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Connect
                </Button>
              </div>
              {calendarsQ.isError && (
                <p className="text-xs text-destructive">
                  Could not load calendars for this account.
                </p>
              )}
            </div>
          )}
          <WatchList
            watches={watchRows}
            emptyLabel="No calendars connected for this space yet."
            onDelete={(id) => del.mutate(id)}
            deleting={del.isPending}
          />
          <p className="text-[11px] text-muted-foreground">
            Agents listen with eventType{' '}
            <code>com.google.calendar.event.created</code> (plus{' '}
            <code>updated</code>/<code>deleted</code>) and a filter on the calendar.
          </p>
        </div>
      </Card>
    </section>
  );
}

/** Shared "Google account" picker for the Chat cards; hidden when there's only
 *  one connected account (the single account is used implicitly). */
function ChatAccountSelect({
  accounts,
  value,
  onChange,
}: {
  accounts: Credential[];
  value: string | undefined;
  onChange: (id: string) => void;
}) {
  if (accounts.length <= 1) return null;
  return (
    <label className="block text-xs text-muted-foreground">
      Google account
      <select
        className="mt-1 w-full text-sm border border-border rounded-md bg-background px-2 py-1.5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {accounts.map((a) => (
          <option key={a.credId} value={a.providerInstanceId}>
            {a.metadata.externalUsername ?? a.metadata.accountName ?? a.credId}
          </option>
        ))}
      </select>
    </label>
  );
}

function SurfaceHeader({
  icon,
  title,
  status,
}: {
  icon: React.ReactNode;
  title: string;
  status: Integration['status'];
}) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <Badge variant={getStatusVariant(status)}>{status}</Badge>
    </div>
  );
}

/**
 * One merged deliveries stream across the org's Google connections. Google has
 * a fixed set of surfaces (Gmail / Chat / Calendar), so we call the
 * per-integration event hooks for each fixed slot (undefined when that surface
 * isn't connected — the hooks no-op), tag every row with its surface, and render
 * a single time-sorted list reusing the shared delivery row + merge logic from
 * IntegrationDeliveriesCard.
 */
function GoogleDeliveriesCard({
  spaceId,
  gmailConn,
  chatConn,
  calendarConn,
}: {
  spaceId: string;
  gmailConn?: Integration;
  chatConn?: Integration;
  calendarConn?: Integration;
}) {
  const gmailHist = useIntegrationEvents(gmailConn?.integrationId);
  const gmailLive = useIntegrationLiveEvents(
    gmailConn?.integrationId,
    gmailConn ? ['space', spaceId, 'integrations', gmailConn.integrationId] : undefined,
  );
  const chatHist = useIntegrationEvents(chatConn?.integrationId);
  const chatLive = useIntegrationLiveEvents(
    chatConn?.integrationId,
    chatConn ? ['space', spaceId, 'integrations', chatConn.integrationId] : undefined,
  );
  const calendarHist = useIntegrationEvents(calendarConn?.integrationId);
  const calendarLive = useIntegrationLiveEvents(
    calendarConn?.integrationId,
    calendarConn
      ? ['space', spaceId, 'integrations', calendarConn.integrationId]
      : undefined,
  );
  const { isConnected } = usePlatformEvents();
  const { data: spaces } = useMeSpaces();

  const spaceNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of spaces?.items ?? []) map.set(s.spaceId, s.name);
    return map;
  }, [spaces]);
  const resolveSpaceName = (id: string) => spaceNameById.get(id);

  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const isLoading =
    (!!gmailConn && gmailHist.isLoading) ||
    (!!chatConn && chatHist.isLoading) ||
    (!!calendarConn && calendarHist.isLoading);

  const rows = useMemo(() => {
    const tag = (
      conn: Integration | undefined,
      hist: IntegrationEvent[] | undefined,
      live: LiveIntegrationEvent[],
      label: string,
    ): DeliveryRow[] =>
      conn
        ? mergeRows(hist, live).map((r) => ({
            ...r,
            // Prefix with the integration id so keys stay unique across surfaces.
            key: `${conn.integrationId}:${r.key}`,
            integrationId: conn.integrationId,
            sourceLabel: label,
          }))
        : [];
    return [
      ...tag(gmailConn, gmailHist.data, gmailLive, 'Gmail'),
      ...tag(chatConn, chatHist.data, chatLive, 'Chat'),
      ...tag(calendarConn, calendarHist.data, calendarLive, 'Calendar'),
    ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [
    gmailConn,
    chatConn,
    calendarConn,
    gmailHist.data,
    gmailLive,
    chatHist.data,
    chatLive,
    calendarHist.data,
    calendarLive,
  ]);

  return (
    <Card>
      <CardHeader
        title="Recent Deliveries"
        description="Gmail, Chat, and Calendar events routed into this space, newest first. Live updates via WebSocket."
        action={<ConnectionIndicator isConnected={isConnected} />}
      />
      {isLoading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No events received yet.</p>
      ) : (
        <div className="rounded-md border divide-y-0">
          {rows.map((row) => (
            <DeliveryRowView
              key={row.key}
              row={row}
              integrationId={row.integrationId!}
              expanded={expandedKey === row.key}
              onToggle={() =>
                setExpandedKey((prev) => (prev === row.key ? null : row.key))
              }
              useEventDetail={useIntegrationEventDetail}
              resolveSpaceName={resolveSpaceName}
              hideTargets
              idMode="event"
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function WatchList({
  watches,
  emptyLabel,
  onDelete,
  deleting,
}: {
  watches: GoogleWatch[];
  emptyLabel: string;
  onDelete: (watchId: string) => void;
  deleting: boolean;
}) {
  if (watches.length === 0) {
    return <p className="text-xs text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <ul className="border border-border rounded-lg divide-y divide-border bg-card">
      {watches.map((w) => (
        <li key={w.watchId} className="flex items-center gap-3 px-3 py-2">
          <span className="flex-1 min-w-0 truncate">
            <span className="text-sm text-foreground">
              {w.displayName || w.resourceId}
            </span>
            {w.displayName && (
              <span className="ml-2 font-mono text-[11px] text-muted-foreground">
                {w.resourceId}
              </span>
            )}
          </span>
          {w.mode && (
            <Badge variant="info">
              {w.mode === 'mention' ? '@Operator only' : 'all messages'}
            </Badge>
          )}
          <Badge variant={getStatusVariant(w.status)}>{w.status}</Badge>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(w.watchId)}
            isLoading={deleting}
            aria-label="Disconnect"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </li>
      ))}
    </ul>
  );
}
